// client/src/components/POICreationPopup.js
import React, { useMemo, useEffect, useState, useLayoutEffect, useRef } from 'react';

function padHexColour(hex) {
  if (hex == null) return '#000000';
  if (typeof hex === 'number') return '#' + hex.toString(16).padStart(6, '0');
  let s = String(hex).trim();
  if (!s.startsWith('#')) s = '#' + s;
  if (s.length === 4) return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  return s.length === 7 ? s : '#000000';
}

function hexToInt(hex) {
  const h = padHexColour(hex);
  return parseInt(h.slice(1), 16);
}

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, { credentials: 'include', ...opts });
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = body?.error || body?.message || `${res.status} ${res.statusText}`;
    const e = new Error(msg);
    e.status = res.status;
    e.body = body;
    throw e;
  }
  return body;
}

function safeStr(v) {
  if (v == null) return '';
  return String(v);
}

function parseJsonMaybe(v, fallback) {
  if (v == null) return fallback;
  if (typeof v === 'object') return v;
  const s = String(v).trim();
  if (!s) return fallback;
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function normalizePoiLayersFromStylesRow(row) {
  const candidates = [
    row?.poi_layers,
    row?.poiLayers,
    row?.poi_layers_json,
    row?.poiLayersJson,
    row?.poi_layer_config,
    row?.poiLayerConfig,
    row?.poi_layer_settings,
    row?.poiLayerSettings,
  ];

  let raw = null;
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') {
      raw = c;
      break;
    }
  }

  const parsed = parseJsonMaybe(raw, []);
  const arr = Array.isArray(parsed) ? parsed : [];

  const input = [];
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i];
    if (it == null) continue;

    if (typeof it === 'string' || typeof it === 'number') {
      const label = String(it).trim();
      if (!label) continue;
      input.push({
        idRaw: null,
        label,
        is_base: i === 0 ? 1 : 0,
        order: i,
      });
      continue;
    }

    if (typeof it === 'object' && !Array.isArray(it)) {
      const idRaw = it.id ?? it.key ?? it.layer_id ?? it.layerId ?? null;
      const labelRaw = it.label ?? it.name ?? it.title ?? it.layer_name ?? it.layerName ?? null;

      const label = labelRaw == null ? '' : String(labelRaw).trim();

      const isBase =
        it.is_base === 1 ||
        it.is_base === true ||
        it.isBase === 1 ||
        it.isBase === true ||
        it.base === 1 ||
        it.base === true ||
        i === 0;

      const order = Number.isFinite(Number(it.order)) ? Number(it.order) : i;

      input.push({
        idRaw: idRaw == null ? null : String(idRaw).trim(),
        label,
        is_base: isBase ? 1 : 0,
        order,
      });
    }
  }

  if (input.length === 0) {
    return [{ id: '0', label: 'Base layer', order: 0, aliases: ['base', 'base layer', 'baselayer'] }];
  }

  let baseIndex = input.findIndex(x => x && (x.is_base === 1 || x.is_base === true));
  if (baseIndex < 0) baseIndex = 0;

  const baseLabel = input[baseIndex] && input[baseIndex].label ? String(input[baseIndex].label).trim() : '';
  const baseAliases = new Set(['0', 'base', 'base layer', 'baselayer']);
  if (input[baseIndex]?.idRaw) baseAliases.add(String(input[baseIndex].idRaw).trim().toLowerCase());

  const others = input
    .filter((_, idx) => idx !== baseIndex)
    .map(x => ({
      label: x && x.label ? String(x.label).trim() : '',
      order: Number.isFinite(Number(x && x.order)) ? Number(x.order) : 0,
      idRaw: x && x.idRaw ? String(x.idRaw).trim() : null,
    }))
    .filter(x => x.label);

  others.sort((a, b) => {
    const ao = Number(a?.order ?? 0);
    const bo = Number(b?.order ?? 0);
    if (ao !== bo) return ao - bo;
    return String(a?.label || '').localeCompare(String(b?.label || ''), undefined, { sensitivity: 'base' });
  });

  const out = [];
  out.push({
    id: '0',
    label: baseLabel || 'Base layer',
    order: 0,
    aliases: Array.from(baseAliases),
  });

  for (let i = 0; i < others.length; i++) {
    const id = String(i + 1);
    const aliases = new Set([id]);
    if (others[i]?.label) aliases.add(String(others[i].label).trim().toLowerCase());
    if (others[i]?.idRaw) aliases.add(String(others[i].idRaw).trim().toLowerCase());
    out.push({
      id,
      label: String(others[i].label).trim(),
      order: i + 1,
      aliases: Array.from(aliases),
    });
  }

  return out;
}

function resolveLayerIdFromValue(value, layers) {
  const raw = safeStr(value).trim();
  if (!raw) return '';

  const lower = raw.toLowerCase();
  const arr = Array.isArray(layers) ? layers : [];

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    if (id === raw) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    if (safeStr(l?.id).trim().toLowerCase() === lower) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    const labelLower = safeStr(l?.label).trim().toLowerCase();
    if (labelLower && labelLower === lower) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    const aliases = Array.isArray(l?.aliases) ? l.aliases : [];
    if (aliases.some(a => safeStr(a).trim().toLowerCase() === lower)) return id;
  }

  if (/^\d+$/.test(raw)) return raw;

  return '';
}

function normalizeVisualStyleForApi(v) {
  const s = String(v ?? '').trim().toLowerCase();
  if (!s) return 'shape_label';

  const map = {
    icon_only: 'icon_only',
    text_only: 'icon_only',
    label_only: 'icon_only',

    icon_text: 'icon_only',

    icon_label: 'icon_label',

    shape_only: 'shape_only',

    shape_text: 'shape_label',
    shape_label: 'shape_label',

    number: 'number',
    numeric: 'number',

    number_label: 'number_label',
    numberlabel: 'number_label',
    number_mouseover: 'number_label',
    number_on_mouseover: 'number_label',

    auto: 'auto',
  };

  return map[s] || 'shape_label';
}

function clampInt(n, min, max, fallback) {
  const v = typeof n === 'number' ? n : parseInt(String(n ?? ''), 10);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, v));
}

function pickFirstFiniteNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n) && !Number.isNaN(n) && n !== 0) return n;
  }
  return null;
}

async function enforcePoiVisualStyle(poiId, mapId, visualStyleForApi) {
  if (!poiId || !mapId || !visualStyleForApi) return;
  const url = `/api/poi/update-visual-style/${poiId}`;
  const payload = {
    map_id: Number(mapId),
    visual_style: visualStyleForApi,
  };
  try {
    await fetchJSON(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.warn('[POI STYLE] update-visual-style failed', {
      url,
      payload,
      status: e?.status,
      body: e?.body,
      message: e?.message,
    });
    throw e;
  }
}

async function enforcePoiMarker(poiId, mapId, markerShapeOrNull, numberValueOrNull) {
  if (!poiId || !mapId) return;
  const url = `/api/poi/update-marker/${poiId}`;
  const payload = {
    map_id: Number(mapId),

    marker_shape: markerShapeOrNull == null ? null : String(markerShapeOrNull),
    markerShape: markerShapeOrNull == null ? null : String(markerShapeOrNull),

    badge_shape: markerShapeOrNull == null ? null : String(markerShapeOrNull),
    badgeShape: markerShapeOrNull == null ? null : String(markerShapeOrNull),

    number_value: numberValueOrNull == null ? null : Number(numberValueOrNull),
    numberValue: numberValueOrNull == null ? null : Number(numberValueOrNull),

    badge_number: numberValueOrNull == null ? null : Number(numberValueOrNull),
    badgeNumber: numberValueOrNull == null ? null : Number(numberValueOrNull),
  };
  try {
    await fetchJSON(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.warn('[POI STYLE] update-marker failed', {
      url,
      payload,
      status: e?.status,
      body: e?.body,
      message: e?.message,
    });
    throw e;
  }
}

async function enforcePoiColour(poiId, mapId, colourOverrideOrNull) {
  if (!poiId || !mapId) return;
  const url = `/api/poi/update-colour/${poiId}`;
  const value = colourOverrideOrNull == null ? null : String(colourOverrideOrNull);
  const payload = {
    map_id: Number(mapId),

    colour_override: value,
    colourOverride: value,

    color_override: value,
    colorOverride: value,
  };
  try {
    await fetchJSON(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.warn('[POI STYLE] update-colour failed', {
      url,
      payload,
      status: e?.status,
      body: e?.body,
      message: e?.message,
    });
    throw e;
  }
}

async function enforcePoiLayer(poiId, mapId, layerKeyOrNull) {
  if (!poiId || !mapId) return;
  const url = `/api/poi/update-layer/${poiId}`;
  const value = layerKeyOrNull == null ? null : String(layerKeyOrNull);
  const payload = {
    map_id: Number(mapId),
    layer_key: value,
    layerKey: value,
  };
  try {
    await fetchJSON(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.warn('[POI STYLE] update-layer failed', {
      url,
      payload,
      status: e?.status,
      body: e?.body,
      message: e?.message,
    });
    throw e;
  }
}

function looksLikeIconUrl(v) {
  const s = String(v ?? '').trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (lower.startsWith('data:image/')) return true;
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (lower.startsWith('/')) return true;
  if (/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(lower)) return true;
  return false;
}

function POICreationPopup(props) {
  const {
    show,
    onClose,
    poiName,
    setPoiName,
    selectedCategory,
    setSelectedCategory,
    selectedType,
    setSelectedType,
    localTypeColour,
    setLocalTypeColour,
    colourLocked,
    handleUnlockColour,
    shape,
    setShape,
    description,
    setDescription,
    poiImageFile,
    setPoiImageFile,
    poiImagePreview,
    setPoiImagePreview,
    poiImageInputRef,
    savePOI,
    selectedTypeDisabled,
    customCategories = [],
    customTypes = [],
    pois = [],
    imageUrl,
    categoryId,
    mapId,
    overlayIdDefault = null,
    userId,
    user,
  } = props;

  const [remoteTypes, setRemoteTypes] = useState([]);
  const [poiFallbackTypes, setPoiFallbackTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [lockOverlay, setLockOverlay] = useState(overlayIdDefault != null);

  const [poiImageUploading, setPoiImageUploading] = useState(false);
  const [poiImageUploadError, setPoiImageUploadError] = useState('');

  const [moveActive, setMoveActive] = useState(() => {
    try {
      return !!window.__PA_POI_MOVE_ACTIVE__;
    } catch {
      return false;
    }
  });

  const closeIssuedRef = useRef(false);

  const [visualStyle, setVisualStyle] = useState('shape_label');
  const [numberValue, setNumberValue] = useState(1);
  const [numberBadgeShape, setNumberBadgeShape] = useState('circle');

  const [allowIndividualPoiColours, setAllowIndividualPoiColours] = useState(false);
  const [poiColour, setPoiColour] = useState(() => padHexColour(localTypeColour || '#ff0000'));

  const [availablePoiLayers, setAvailablePoiLayers] = useState([]);
  const [selectedPoiLayer, setSelectedPoiLayer] = useState('');
  const [lockPoiLayer, setLockPoiLayer] = useState(false);

  const mapStylesCacheRef = useRef({
    mapId: null,
    ok: null,
    tried: false,
    row: null,
  });

  const lockPoiLayerRef = useRef(lockPoiLayer);
  const poiColourRef = useRef(poiColour);

  useEffect(() => {
    lockPoiLayerRef.current = lockPoiLayer;
  }, [lockPoiLayer]);

  useEffect(() => {
    poiColourRef.current = poiColour;
  }, [poiColour]);

  useEffect(() => {
    function onMoveOpen() {
      try {
        window.__PA_POI_MOVE_ACTIVE__ = true;
      } catch {}
      setMoveActive(true);
    }

    function onMoveClose() {
      try {
        window.__PA_POI_MOVE_ACTIVE__ = false;
      } catch {}
      setMoveActive(false);
    }

    try {
      window.addEventListener('pa-poi-move-ui-open', onMoveOpen);
      window.addEventListener('pa-poi-move-ui-close', onMoveClose);
    } catch {}

    try {
      window.addEventListener('pa-poi-move-start', onMoveOpen);
    } catch {}

    return () => {
      try {
        window.removeEventListener('pa-poi-move-ui-open', onMoveOpen);
        window.removeEventListener('pa-poi-move-ui-close', onMoveClose);
      } catch {}
      try {
        window.removeEventListener('pa-poi-move-start', onMoveOpen);
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!show) {
      closeIssuedRef.current = false;
    }
  }, [show]);

  useLayoutEffect(() => {
    if (!show) return;
    if (!moveActive) return;
    if (closeIssuedRef.current) return;

    closeIssuedRef.current = true;
    try {
      if (typeof onClose === 'function') onClose();
    } catch {}
  }, [show, moveActive, onClose]);

  useEffect(() => {
    if (!show) return;
    setPoiImageUploadError('');
    setVisualStyle('shape_label');
    setNumberValue(1);
    setNumberBadgeShape('circle');
    setPoiColour(padHexColour(localTypeColour || '#ff0000'));
  }, [show, localTypeColour]);

  const norm = v => String(v ?? '').trim().toLowerCase();
  const selectedCategoryName = useMemo(() => String(selectedCategory ?? '').trim(), [selectedCategory]);
  const selectedTypeName = useMemo(() => String(selectedType ?? '').trim(), [selectedType]);
  const selectedCategoryIdStr = useMemo(() => (categoryId == null ? '' : String(categoryId).trim()), [categoryId]);

  const selectedTypeObject = useMemo(() => {
    if (!selectedTypeName) return null;

    if (Array.isArray(customTypes) && customTypes.length) {
      const typeLower = norm(selectedTypeName);

      const candidates = customTypes.filter(t => {
        const name = String(t?.name ?? t?.type ?? '').trim();
        if (!name) return false;
        if (norm(name) !== typeLower) return false;

        if (selectedCategoryIdStr) {
          return String(t?.category_id ?? '').trim() === selectedCategoryIdStr;
        }

        if (selectedCategoryName) {
          const catName = String(t?.category ?? t?.category_name ?? '').trim();
          if (catName && norm(catName) === norm(selectedCategoryName)) return true;
          if (!catName) return true;
          return false;
        }

        return true;
      });

      if (candidates.length === 1) return candidates[0];
      if (candidates.length > 1) {
        const withIcon = candidates.find(t =>
          looksLikeIconUrl(t?.icon_url ?? t?.iconUrl ?? t?.icon_file ?? t?.iconFile),
        );
        return withIcon || candidates[0];
      }
    }

    return null;
  }, [customTypes, selectedTypeName, selectedCategoryName, selectedCategoryIdStr]);

  const hasSelectedTypeIcon = useMemo(() => {
    const t = selectedTypeObject;
    const iconRaw = t?.icon_url ?? t?.iconUrl ?? t?.icon_file ?? t?.iconFile ?? '';
    return looksLikeIconUrl(iconRaw);
  }, [selectedTypeObject]);

  const hasUploadedIcon = useMemo(() => {
    if (poiImageFile) return true;
    if (looksLikeIconUrl(poiImagePreview)) return true;
    if (looksLikeIconUrl(imageUrl)) return true;
    return false;
  }, [poiImageFile, poiImagePreview, imageUrl]);

  const hasIconForMarkerStyle = useMemo(() => {
    return !!(hasSelectedTypeIcon || hasUploadedIcon);
  }, [hasSelectedTypeIcon, hasUploadedIcon]);

  useEffect(() => {
    if (!show) return;
    if (String(visualStyle || '').trim().toLowerCase() !== 'icon_label') return;
    if (hasIconForMarkerStyle) return;
    setVisualStyle('icon_only');
  }, [show, visualStyle, hasIconForMarkerStyle]);

  const typesFromProps = useMemo(() => {
    if (!selectedCategoryName) return [];
    if (!Array.isArray(customTypes) && customTypes && typeof customTypes === 'object') {
      if (Array.isArray(customTypes[selectedCategoryName])) {
        return customTypes[selectedCategoryName].map(String);
      }
      const hit = Object.keys(customTypes).find(k => norm(k) === norm(selectedCategoryName));
      if (hit && Array.isArray(customTypes[hit])) return customTypes[hit].map(String);
      return [];
    }
    if (Array.isArray(customTypes) && customTypes.length) {
      let matches = [];
      if (categoryId != null && categoryId !== '') {
        matches = customTypes.filter(t => String(t?.category_id) === String(categoryId));
      } else {
        matches = customTypes.filter(t => norm(t?.category || t?.category_name) === norm(selectedCategoryName));
      }
      const names = matches.map(t => String(t?.name ?? t?.type ?? '')).filter(Boolean);
      return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }
    if (Array.isArray(pois) && pois.length) {
      const names = pois
        .filter(p => norm(p?.category) === norm(selectedCategoryName))
        .map(p => String(p?.type ?? ''))
        .filter(Boolean);
      return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }
    return [];
  }, [customTypes, pois, selectedCategoryName, categoryId]);

  async function fetchCategoryIdByName(mId, catName) {
    const res = await fetch(`/api/category-edit/categories/${mId}`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    const rows = Array.isArray(data?.categories) ? data.categories : [];
    const hit = rows.find(r => norm(r?.name) === norm(catName));
    return hit?.id ?? null;
  }

  useEffect(() => {
    let cancelled = false;

    async function loadTypesViaEndpoint() {
      if (!show) return;
      if (moveActive) return;
      if (!mapId || !selectedCategoryName) return;
      if (typesFromProps.length > 0) {
        if (!cancelled) {
          setRemoteTypes([]);
          setPoiFallbackTypes([]);
        }
        return;
      }
      try {
        setLoadingTypes(true);
        let catId = categoryId && String(categoryId).trim() !== '' ? Number(categoryId) : null;
        if (!catId) catId = await fetchCategoryIdByName(mapId, selectedCategoryName);
        if (!catId) {
          if (!cancelled) setRemoteTypes([]);
          return;
        }
        const res = await fetch(`/api/category-edit/types/${mapId}/${catId}`, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setRemoteTypes([]);
          return;
        }
        const data = await res.json();
        const names = (Array.isArray(data?.types) ? data.types : []).map(t => String(t?.name ?? '')).filter(Boolean);
        if (!cancelled) {
          const uniq = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
          setRemoteTypes(uniq);
        }
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    }

    loadTypesViaEndpoint();
    return () => {
      cancelled = true;
    };
  }, [show, mapId, categoryId, selectedCategoryName, typesFromProps.length, moveActive]);

  useEffect(() => {
    let cancelled = false;

    async function loadTypesFromPois() {
      if (!show) return;
      if (moveActive) return;
      if (!mapId || !selectedCategoryName) return;
      if (typesFromProps.length > 0 || remoteTypes.length > 0) {
        if (!cancelled) setPoiFallbackTypes([]);
        return;
      }
      try {
        const res = await fetch(`/api/poi/all/${mapId}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const rows = Array.isArray(data?.pois) ? data.pois : [];
        const names = rows
          .filter(p => norm(p?.category) === norm(selectedCategoryName))
          .map(p => String(p?.type ?? ''))
          .filter(Boolean);
        const uniq = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
        if (!cancelled) setPoiFallbackTypes(uniq);
      } catch {}
    }

    loadTypesFromPois();
    return () => {
      cancelled = true;
    };
  }, [show, mapId, selectedCategoryName, typesFromProps.length, remoteTypes.length, moveActive]);

  const typesForCategory =
    typesFromProps.length > 0 ? typesFromProps : remoteTypes.length > 0 ? remoteTypes : poiFallbackTypes;

  useEffect(() => {
    let cancelled = false;

    async function loadPoiLayerAndColourRules() {
      if (!show) return;
      if (!mapId) return;

      const id = String(mapId).trim();
      const cache = mapStylesCacheRef.current;

      if (cache.mapId === id && cache.tried) {
        if (cache.ok && cache.row) {
          const row = cache.row;
          const enabled = Number(row?.poi_allow_individual_colours) === 1;
          setAllowIndividualPoiColours(enabled);

          const layers = normalizePoiLayersFromStylesRow(row);
          setAvailablePoiLayers(layers);

          const dRaw = safeStr(row?.poi_layer_default ?? '').trim();
          const d = resolveLayerIdFromValue(dRaw, layers);
          if (d && !lockPoiLayerRef.current) {
            setSelectedPoiLayer(d);
            setLockPoiLayer(true);
          }

          if (enabled) {
            const seed = padHexColour(poiColourRef.current || localTypeColour || '#ff0000');
            setPoiColour(seed);
          }
        } else {
          setAllowIndividualPoiColours(false);
          setAvailablePoiLayers([]);
        }
        return;
      }

      mapStylesCacheRef.current = { mapId: id, ok: null, tried: true, row: null };

      try {
        const row = await fetchJSON(`/api/mapStyles/${Number(mapId)}`);
        if (cancelled) return;

        mapStylesCacheRef.current = { mapId: id, ok: true, tried: true, row };

        const enabled = Number(row?.poi_allow_individual_colours) === 1;
        setAllowIndividualPoiColours(enabled);

        const layers = normalizePoiLayersFromStylesRow(row);
        setAvailablePoiLayers(layers);

        const dRaw = safeStr(row?.poi_layer_default ?? '').trim();
        const d = resolveLayerIdFromValue(dRaw, layers);
        if (d && !lockPoiLayerRef.current) {
          setSelectedPoiLayer(d);
          setLockPoiLayer(true);
        }

        if (enabled) {
          const seed = padHexColour(poiColourRef.current || localTypeColour || '#ff0000');
          setPoiColour(seed);
        }
      } catch {
        if (cancelled) return;
        mapStylesCacheRef.current = { mapId: id, ok: false, tried: true, row: null };
        setAllowIndividualPoiColours(false);
        setAvailablePoiLayers([]);
      }
    }

    loadPoiLayerAndColourRules();
    return () => {
      cancelled = true;
    };
  }, [show, mapId, localTypeColour]);

  async function fetchCurrentUserIdFromApi() {
    const candidates = ['/api/user', '/api/user/me', '/api/user/current', '/api/auth/me', '/api/auth/user'];
    for (const url of candidates) {
      try {
        const data = await fetchJSON(url);
        const u = data?.user ?? data?.me ?? data?.profile ?? data;
        const id =
          pickFirstFiniteNumber(u?.id, u?.user_id, u?.userId, data?.id, data?.user_id, data?.userId) || null;
        if (id) return id;
      } catch {}
    }
    return null;
  }

  async function resolveEffectiveUserId() {
    const u = user;
    const nested = u?.user || u?.profile || u?.account || null;

    const direct = pickFirstFiniteNumber(
      userId,
      u?.id,
      u?.user_id,
      u?.userId,
      u?.uid,
      nested?.id,
      nested?.user_id,
      nested?.userId,
      nested?.uid,
    );

    if (direct) return direct;

    const fetched = await fetchCurrentUserIdFromApi();
    if (fetched) return fetched;

    return null;
  }

  async function uploadPoiImage(poiIdOrNull) {
    setPoiImageUploadError('');

    if (!poiImageFile) return null;
    if (!mapId) throw new Error('Missing mapId for image upload');

    const inferredUserId = await resolveEffectiveUserId();
    if (!inferredUserId || Number.isNaN(inferredUserId)) {
      throw new Error('Missing userId for image upload');
    }

    const okType = /^image\/(png|jpeg|jpg|gif|webp)$/i.test(String(poiImageFile.type || ''));
    if (!okType) throw new Error('POI image must be PNG/JPG/GIF/WebP');

    const maxBytes = 1 * 1024 * 1024;
    if (poiImageFile.size && poiImageFile.size > maxBytes) throw new Error('POI image is too large (max 1MB)');

    const fd = new FormData();
    fd.append('image', poiImageFile);
    fd.append('map_id', String(mapId));
    fd.append('user_id', String(inferredUserId));
    if (poiIdOrNull != null && String(poiIdOrNull).trim() !== '' && !Number.isNaN(Number(poiIdOrNull))) {
      fd.append('poi_id', String(poiIdOrNull));
    }

    setPoiImageUploading(true);
    try {
      const res = await fetch('/api/poi/upload-image', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });

      let body = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }

      if (!res.ok) {
        const msg = body?.error || body?.message || `${res.status} ${res.statusText}`;
        throw new Error(msg);
      }

      const url = typeof body?.image_url === 'string' ? body.image_url : '';
      if (!url) throw new Error('Upload succeeded but no image_url returned');
      return url;
    } finally {
      setPoiImageUploading(false);
    }
  }

  const handleSaveClick = async () => {
    setPoiImageUploadError('');

    const overlayId = lockOverlay ? (overlayIdDefault != null ? overlayIdDefault : 1) : null;
    const baseImageUrl = imageUrl ? imageUrl.trim() : '';
    const visualStyleForApi = normalizeVisualStyleForApi(visualStyle);

    const effectiveNumberValue = clampInt(numberValue, 1, 125, 1);
    const effectiveNumberBadgeShape = String(numberBadgeShape || 'circle').trim().toLowerCase() || 'circle';

    const chosenShape = String(shape || 'circle').trim().toLowerCase() || 'circle';

    const isNumberStyle = visualStyleForApi === 'number' || visualStyleForApi === 'number_label';

    const colourToSend = allowIndividualPoiColours ? padHexColour(poiColour || localTypeColour || '#ff0000') : null;

    const selectedLayerUiRaw = safeStr(selectedPoiLayer).trim();
    const resolvedLayer = resolveLayerIdFromValue(selectedLayerUiRaw, availablePoiLayers);
    const poiLayerToSend = lockPoiLayer && resolvedLayer ? resolvedLayer : null;

    const createStylePayload = (() => {
      const base = {
        visual_style: visualStyleForApi,

        marker_shape: null,
        markerShape: null,

        number_value: null,
        numberValue: null,

        colour_override: colourToSend,
        colourOverride: colourToSend,
        color_override: colourToSend,
        colorOverride: colourToSend,

        layer_key: poiLayerToSend,
        layerKey: poiLayerToSend,

        overlay_id: overlayId,
        overlayId: overlayId,
      };

      if (isNumberStyle) {
        base.marker_shape = effectiveNumberBadgeShape;
        base.markerShape = effectiveNumberBadgeShape;
        base.badge_shape = effectiveNumberBadgeShape;
        base.badgeShape = effectiveNumberBadgeShape;

        base.number_value = effectiveNumberValue;
        base.numberValue = effectiveNumberValue;
        base.badge_number = effectiveNumberValue;
        base.badgeNumber = effectiveNumberValue;
      } else {
        base.marker_shape = chosenShape;
        base.markerShape = chosenShape;
      }

      return base;
    })();

    let poiResult = null;

    try {
      if (poiImageFile) {
        const uploadedUrl = await uploadPoiImage(null);
        poiResult = await savePOI(poiName, uploadedUrl, description, createStylePayload);

        try {
          setPoiImageFile(null);
          setPoiImagePreview(null);
          if (poiImageInputRef?.current) poiImageInputRef.current.value = '';
        } catch {}
      } else {
        poiResult = await savePOI(poiName, baseImageUrl, description, createStylePayload);
      }
    } catch (err) {
      setPoiImageUploadError(err?.message || 'Failed to save POI');
      return;
    }

    const poiId =
      pickFirstFiniteNumber(poiResult?.id, poiResult?.poi_id, poiResult?.poiId, poiResult?.poi?.id, poiResult?.data?.id) ||
      null;

    if (!poiId || !mapId) {
      setPoiImageUploadError('POI created but no poiId returned; cannot save marker style fields.');
      console.warn('[POI STYLE] Missing poiId/mapId after create', { poiResult, poiId, mapId });
      return;
    }

    try {
      await enforcePoiVisualStyle(poiId, mapId, visualStyleForApi);

      if (isNumberStyle) {
        await enforcePoiMarker(poiId, mapId, effectiveNumberBadgeShape, effectiveNumberValue);
      } else {
        await enforcePoiMarker(poiId, mapId, chosenShape, null);
      }

      if (allowIndividualPoiColours) {
        await enforcePoiColour(poiId, mapId, colourToSend);
      }

      if (poiLayerToSend) {
        await enforcePoiLayer(poiId, mapId, poiLayerToSend);
      }
    } catch (e) {
      const msg =
        e?.message ||
        'POI was created, but saving marker style fields failed. Check console warnings for which endpoint/field is wrong.';
      setPoiImageUploadError(msg);
      return;
    }

    if (typeof onClose === 'function') onClose();
  };

  if (!show) return null;
  if (moveActive) return null;

  const showNumberControls = ['number', 'number_label'].includes(String(visualStyle || '').trim().toLowerCase());
  const hasLayersUi = Array.isArray(availablePoiLayers) && availablePoiLayers.length > 0;

  const ALL_SHAPES = [
    { value: 'circle', label: 'Circle' },
    { value: 'square', label: 'Square' },
    { value: 'triangle', label: 'Triangle' },
    { value: 'diamond', label: 'Diamond' },
    { value: 'hex', label: 'Hex' },
    { value: 'octagon', label: 'Octagon' },
    { value: 'pin', label: 'Map Pin' },
    { value: 'flag', label: 'Flag' },
    { value: 'crosshair', label: 'Crosshair' },
    { value: 'shield', label: 'Shield' },
    { value: 'badge', label: 'Badge' },
    { value: 'tag', label: 'Tag' },
    { value: 'key', label: 'Key' },
    { value: 'lock', label: 'Lock' },
    { value: 'unlock', label: 'Unlock' },
    { value: 'info', label: 'Info' },
    { value: 'plus', label: 'Plus' },
    { value: 'bolt', label: 'Bolt' },
    { value: 'drop', label: 'Drop' },
    { value: 'wrench', label: 'Wrench' },
    { value: 'cone', label: 'Cone' },
    { value: 'pill', label: 'Pill' },
    { value: 'star', label: 'Star' },
    { value: 'heart', label: 'Heart' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 360,
        maxHeight: '80vh',
        overflowY: 'auto',
        background: '#23242a',
        borderRadius: 16,
        boxShadow: '0 6px 24px #000b',
        padding: 12,
        color: '#fff',
        border: '1.5px solid #23252a',
        zIndex: 2002,
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={e => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 8,
          right: 10,
          background: '#444',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '4px 9px',
          cursor: 'pointer',
          zIndex: 2100,
        }}
      >
        ✕
      </button>

      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>Create New POI</div>

      <div style={{ marginBottom: 7 }}>
        <label>Name:</label>
        <br />
        <input
          type="text"
          value={poiName}
          onChange={e => setPoiName(e.target.value)}
          placeholder="Enter POI name (optional)"
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <label>Category:</label>
        <br />
        <input
          type="text"
          value={selectedCategory}
          onChange={e => {
            setSelectedCategory(e.target.value);
            setSelectedType('');
          }}
          list="category-options"
          placeholder="Enter or select category"
          style={{ width: '100%' }}
        />
        <datalist id="category-options">
          {customCategories.map(cat => {
            const val = typeof cat === 'string' ? cat : String(cat?.name ?? '');
            return <option key={val} value={val} />;
          })}
        </datalist>
      </div>

      <div style={{ marginTop: 7 }}>
        <label>Type:</label>
        <br />
        <input
          type="text"
          value={selectedType}
          onChange={e => setSelectedType(e.target.value)}
          list="type-options"
          placeholder={loadingTypes ? 'Loading types...' : selectedCategoryName ? 'Enter or select type' : 'Select a category first'}
          disabled={!selectedCategoryName || loadingTypes}
          style={{ width: '100%' }}
        />
        <datalist id="type-options">
          {typesForCategory.map(type => (
            <option key={type} value={type} />
          ))}
        </datalist>
      </div>

      <div style={{ marginTop: 7 }}>
        <label>Description:</label>
        <br />
        <textarea
          rows="3"
          placeholder="Optional description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginTop: 7 }}>
        <label>Marker Style:</label>
        <br />
        <select value={visualStyle} onChange={e => setVisualStyle(e.target.value)} style={{ width: '100%' }}>
          <option value="shape_label">Shape + Label</option>
          <option value="shape_only">Shape Only</option>
          <option value="auto">Shape Only - Label on Mouseover</option>
          <option value="number">Number</option>
          <option value="number_label">Number - Label on Mouseover</option>
          <option value="icon_only">Icon/Label Only </option>
          {hasIconForMarkerStyle && <option value="icon_label">Icon - Label on Mouseover</option>}
        </select>
      </div>

      {showNumberControls ? (
        <>
          <div style={{ marginTop: 7 }}>
            <label>Number:</label>
            <br />
            <select
              value={clampInt(numberValue, 1, 125, 1)}
              onChange={e => setNumberValue(clampInt(e.target.value, 1, 125, 1))}
              style={{ width: '100%' }}
            >
              {Array.from({ length: 99 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
              {Array.from({ length: 26 }, (_, i) => i).map(i => {
                const value = 100 + i;
                const label = String.fromCharCode(65 + i);
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div style={{ marginTop: 7 }}>
            <label>Badge Shape:</label>
            <br />
            <select
              value={String(numberBadgeShape || 'circle').trim().toLowerCase() || 'circle'}
              onChange={e => setNumberBadgeShape(e.target.value)}
              style={{ width: '100%' }}
            >
              {ALL_SHAPES.map(s => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 7 }}>
          <label>Shape:</label>
          <br />
          <select value={shape} onChange={e => setShape(e.target.value)} style={{ width: '100%' }}>
            {ALL_SHAPES.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {allowIndividualPoiColours && (
        <div style={{ marginTop: 7 }}>
          <label>POI Colour Override:</label>
          <br />
          <input
            type="color"
            value={padHexColour(poiColour || localTypeColour || '#ff0000')}
            onChange={e => setPoiColour(e.target.value)}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ marginLeft: 10, fontSize: 13, color: '#ffe670' }}>
            {String(padHexColour(poiColour || localTypeColour || '#ff0000') || '').toUpperCase()}
          </span>
        </div>
      )}

      {hasLayersUi && (
        <div style={{ marginTop: 7 }}>
          <label>POI Layer:</label>
          <br />
          <select
            value={lockPoiLayer ? resolveLayerIdFromValue(selectedPoiLayer, availablePoiLayers) : ''}
            onChange={e => {
              const v = resolveLayerIdFromValue(String(e.target.value || '').trim(), availablePoiLayers);
              setSelectedPoiLayer(v);
              setLockPoiLayer(!!v);
            }}
            style={{ width: '100%' }}
          >
            <option value="">All layers</option>
            {availablePoiLayers.map(l => (
              <option key={safeStr(l?.id)} value={safeStr(l?.id)}>
                {safeStr(l?.label || l?.id)}
              </option>
            ))}
          </select>

          <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, marginTop: 6 }}>
            <input
              type="checkbox"
              checked={!!lockPoiLayer}
              onChange={e => {
                const next = !!e.target.checked;
                if (!next) {
                  setLockPoiLayer(false);
                  return;
                }
                const current = resolveLayerIdFromValue(safeStr(selectedPoiLayer).trim(), availablePoiLayers);
                const first =
                  Array.isArray(availablePoiLayers) && availablePoiLayers.length
                    ? safeStr(availablePoiLayers[0]?.id).trim()
                    : '';
                const pick = current || first || '';
                setSelectedPoiLayer(pick);
                setLockPoiLayer(!!pick);
              }}
              style={{ marginRight: 6 }}
            />
            <span>{lockPoiLayer ? 'Lock to selected layer' : 'Show on all layers'}</span>
          </label>
        </div>
      )}

      <div style={{ marginTop: 7 }}>
        <label>POI Image (PNG/JPG/GIF/WebP, max 1MB):</label>
        <br />
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          ref={poiImageInputRef}
          onChange={e => {
            const file = e.target.files[0];
            setPoiImageFile(file || null);
            if (file) {
              const reader = new FileReader();
              reader.onload = ev => setPoiImagePreview(ev.target.result);
              reader.readAsDataURL(file);
            } else {
              setPoiImagePreview(null);
            }
            setPoiImageUploadError('');
          }}
          style={{ marginBottom: 6, width: '100%' }}
        />
        {poiImagePreview && (
          <div style={{ marginTop: 6 }}>
            <img
              src={poiImagePreview}
              alt="POI image preview"
              style={{ width: 64, height: 64, background: '#222', border: '1px solid #999', borderRadius: 5 }}
            />
            <button
              type="button"
              onClick={() => {
                setPoiImageFile(null);
                setPoiImagePreview(null);
                setPoiImageUploadError('');
                if (poiImageInputRef.current) poiImageInputRef.current.value = '';
              }}
              style={{
                marginLeft: 15,
                background: '#f44c2b',
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                padding: '2px 8px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Remove
            </button>
          </div>
        )}
        {poiImageUploading && <div style={{ marginTop: 6, fontSize: 13, color: '#ffe670' }}>Uploading POI image...</div>}
        {!!poiImageUploadError && <div style={{ marginTop: 6, fontSize: 13, color: '#ff8a80' }}>{poiImageUploadError}</div>}
      </div>

      <div style={{ marginTop: 7 }}>
        <label style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={lockOverlay}
            onChange={e => setLockOverlay(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          <span>{overlayIdDefault == null ? 'Lock POI to base map (no overlay active)' : 'Lock POI to current overlay'}</span>
        </label>
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          onClick={handleSaveClick}
          disabled={selectedTypeDisabled || poiImageUploading}
          style={{
            background: '#4cc153',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            padding: '6px 18px',
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          {poiImageUploading ? 'Uploading...' : 'Save POI'}
        </button>
      </div>

      <div style={{ marginTop: 10, opacity: 0.55, fontSize: 12 }}>
        <span style={{ marginRight: 10 }}>Type colour is still sent to the legacy create route:</span>
        <span style={{ color: '#ffe670' }}>{String(padHexColour(localTypeColour || '#ff0000')).toUpperCase()}</span>
        {colourLocked && (
          <button
            type="button"
            onClick={handleUnlockColour}
            style={{
              marginLeft: 10,
              background: '#ffe670',
              color: '#181818',
              border: 'none',
              borderRadius: 7,
              padding: '2px 10px',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Change
          </button>
        )}
        {!colourLocked && (
          <input
            type="color"
            value={localTypeColour}
            onChange={e => setLocalTypeColour(e.target.value)}
            style={{ marginLeft: 10, cursor: 'pointer' }}
          />
        )}
      </div>
    </div>
  );
}

export default POICreationPopup;
export { POICreationPopup };
